import { useEffect, useState } from "react";
import type { EmojiClickData as _EmojiClickData, PickerProps } from "emoji-picker-react";
import { loadEmojiPicker } from "@/extension/utils/lazy-lib";

/**
 * Stateful lazy boundary over emoji-picker-react's <EmojiPicker/> component.
 *
 * emoji-picker-react (+ flairup + inline emoji data, ~161KB) renders in MANY
 * scattered spots (message composer, chatbot editor, task/board icon pickers,
 * team-inbox input, labels) — always inside a MUI Menu/Popover that only mounts
 * this component when the user opens the picker. Direct `import EmojiPicker from
 * 'emoji-picker-react'` at those sites is what pins the library into app.js at
 * startup; routing every site through this boundary drops it from app.js and loads
 * it on first open from the lazy/emoji-picker.js WAR bundle (which shares app.js's
 * React via the __BT_REACT__ bridge — see loadEmojiPicker in
 * src/extension/utils/lazy-lib.ts).
 *
 * Stateful wrapper (like lazy-lottie) rather than React.lazy + Suspense: the
 * picker mounts in unrelated trees with no shared Suspense boundary, and each
 * caller already sizes it via its Menu/Popover. A stateful loader paints a
 * correctly-sized placeholder and swaps in the real picker once the shared
 * (memoized) loader resolves — no suspense flash, no risk of suspending an
 * unexpected ancestor.
 */

// Re-exported so consumers get the type from the boundary and never import
// anything runtime from emoji-picker-react (a value import would re-pin the lib).
export type EmojiClickData = _EmojiClickData;

/**
 * Mirrors emoji-picker-react's Theme string-enum (LIGHT='light', DARK='dark',
 * AUTO='auto') so consumers can set the `theme` prop WITHOUT importing the ~161KB
 * library just for the constant. Values verified against
 * emoji-picker-react/dist/emoji-picker-react.esm.js.
 */
export const Theme = { LIGHT: "light", DARK: "dark", AUTO: "auto" } as const;
export type Theme = (typeof Theme)[keyof typeof Theme];

// theme is widened to string here because consumers pass the local Theme const
// (string literals), which is not assignable to the library's nominal enum type.
export type LazyEmojiPickerProps = Omit<PickerProps, "theme"> & { theme?: string };

// Shared across every LazyEmojiPicker instance in this document: once the first
// picker resolves the component, later ones render it synchronously.
let cachedEmojiPicker: any = null;

const LazyEmojiPicker: React.FC<LazyEmojiPickerProps> = (props) => {
  // Lazy INITIALIZER (`() => cachedEmojiPicker`), never the value directly: when the
  // cache is warm `cachedEmojiPicker` is a function component, and `useState(fn)`
  // would treat it as a lazy initializer and CALL it (rendering the picker with no
  // props → crash). The initializer returns the component without invoking it. (Same
  // reason the setter below uses `() => Comp`.)
  const [Picker, setPicker] = useState<any>(() => cachedEmojiPicker);

  useEffect(() => {
    if (cachedEmojiPicker) return;
    let mounted = true;
    loadEmojiPicker()
      .then((Comp) => {
        cachedEmojiPicker = Comp;
        // Functional updater so React doesn't treat the function component as a
        // lazy state initializer.
        if (mounted) setPicker(() => Comp);
      })
      .catch(() => {
        /* loadEmojiPicker already logs + guards its own fallback */
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!Picker) {
    // Sized placeholder matching emoji-picker-react's default footprint so the
    // picker popping in causes no layout shift inside the Menu/Popover.
    return (
      <div
        aria-hidden="true"
        style={{ width: 350, height: 450 }}
      />
    );
  }

  return <Picker {...props} />;
};

export default LazyEmojiPicker;
